import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { toFullName } from '@repo/toolkit/to-full-name'
import Badge from '@repo/ui/badge'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { toast } from 'sonner'

import { getCseRegistration } from '../../../../api/enrollments/add-cse-registration'
import { clearPromoCode } from '../../../../api/enrollments/clear-promocode'
import { comparePropositionWithMainProposition } from '../../../../api/enrollments/compare-proposition-with-main-proposition'
import { getEnrollment } from '../../../../api/enrollments/get-enrollment'
import { getEnrollmentFriends } from '../../../../api/enrollments/get-enrollment-friends'
import { getEnrollmentProposition } from '../../../../api/enrollments/get-enrollment-propostion'
import { updateEnrollment } from '../../../../api/enrollments/update-enrollment'
import { updateEnrollmentProposition } from '../../../../api/enrollments/update-enrollment-proposition'
import { useObserver } from '../../../../lib/contexts/observer'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../../lib/hooks/use-translate'
import { ApplicationStatusEnum } from '../../../../lib/types/enums/enrollment/application-status.enum'
import { enrollmentPropositionDetailsFields } from '../../../../lib/types/fields/enrollments/enrollment'
import { EnrollmentProperties } from '../../../../lib/types/models/enrollment/enrollment.properties'
import { EnrollmentInput } from '../../../../lib/types/models/enrollment/enrollment-input'
import { EnrollmentPropositionLight } from '../../../../lib/types/models/enrollment/enrollment-light'
import { EnrollmentPropositionProperties } from '../../../../lib/types/models/enrollment-proposition/enrollment-proposition.properties'
import { EnrollmentPropositionInput } from '../../../../lib/types/models/enrollment-proposition/enrollment-proposition-input'
import { InsuranceProperties } from '../../../../lib/types/models/insurance/properties.insurance'
import { TripProperties } from '../../../../lib/types/models/trip/properties.trip'
import { UpdateInput } from '../../../../lib/types/utils'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import { getTranslatedName } from '../../../../lib/utils/functions/translation.functions'
import DetailsEditCard from '../../../elements/details-edit-card'
import { getDurationInDaysString } from '../../products/utils'
import EnrollmentPropositionInsuranceModal from '../enrollment-proposition-insurance-modal'
import EnrollmentPropositionFormModal from '../enrolment-create-proposition-steps/enrollment-proposition-form-modal'
import {
  mapDeclaredFriendsToFriendInput,
  mapEnrollmentPropositionInputToRequest,
  mapEnrollmentPropositionToInput
} from '../utils/enrollments-utils'
import EnrollmentPropositionSummary, {
  EnrollmentPropositionSummarySkeleton
} from './enrollment-proposition-summary'

type Props =
  | {
      mode: 'normal'
      status?: ApplicationStatusEnum
      input: EnrollmentPropositionInput
      onPropositionChange: (proposition: EnrollmentPropositionInput) => void
      enrollmentId?: never // Ensure these are not allowed in 'normal' mode
      propositionId?: never
      country?: string
      mainProposition?: EnrollmentPropositionLight
      onSuccess?: never
    }
  | {
      mode: 'instant'
      status?: ApplicationStatusEnum
      enrollmentId: number
      propositionId: number
      input?: never // Ensure these are not allowed in 'instant' mode
      onPropositionChange?: never
      country?: never
      mainProposition?: EnrollmentPropositionLight
      onSuccess?: () => void
    }

export interface EnrollmentPropositionDetailsRef {
  openInsuranceModal: () => void
}

export const EnrollmentPropositionDetailsSkeleton = () => (
  <div className="grid grid-cols-3 gap-4 mt-5">
    <div className="col-span-2 grid grid-cols-2 gap-3">
      <DetailsEditCard isLoading />
      <DetailsEditCard isLoading />
      <DetailsEditCard isLoading />
      <DetailsEditCard isLoading />
    </div>
    <EnrollmentPropositionSummarySkeleton />
  </div>
)

const EnrollmentPropositionDetails = forwardRef<EnrollmentPropositionDetailsRef, Props>(
  (
    {
      mode,
      enrollmentId,
      propositionId,
      country,
      input,
      mainProposition,
      status,
      onSuccess,
      onPropositionChange
    },
    ref
  ) => {
    const { t } = useTranslate()
    const [proposition, setProposition] = useState(input)
    const [index, setIndex] = useState(0)
    const { modalState, toggleModal, closeModel } = useModalManager<
      'select-insurance' | 'create-journey'
    >(['select-insurance', 'create-journey'])
    const hasDifferentProposition = mainProposition?.id !== propositionId
    const queryClient = useQueryClient()

    const { data: cseRegistration, refetch: refetchCseRegistration } = useQuery({
      queryKey: ['cse-registration', enrollmentId],
      queryFn: () => {
        if (enrollmentId) return getCseRegistration(enrollmentId)
      },
      enabled: !!enrollmentId
    })

    const cseCode = enrollmentId ? cseRegistration?.cse.code : (proposition?.cseCode ?? undefined)

    const { mutateAsync, isPending: updateEnrollmentPropositionPending } = useMutation({
      mutationFn: updateEnrollmentProposition,
      onError: showAxiosError
    })

    const { mutate: clearPromoCodeMutation, isPending: isClearPromoCodePending } = useMutation({
      mutationFn: clearPromoCode,
      onSuccess: () => {
        setProposition((prev) => ({ ...prev, promoCode: null }))
        toast.success('Code promo supprimé avec succès')
      },
      onError: showAxiosError
    })

    const { mutateAsync: updateEnrollmentMutation, isPending: updateEnrollmentPending } =
      useMutation({
        mutationFn: (input: UpdateInput<EnrollmentInput>) => updateEnrollment(input, ['id']),
        onError: showAxiosError
      })

    const { data, isFetching } = useQuery({
      queryKey: ['enrollment-proposition', propositionId],
      queryFn: async () => {
        if (enrollmentId && propositionId) {
          const result = await getEnrollmentProposition({ enrollmentId, propositionId })
          const friends = await getEnrollmentFriends(enrollmentId)

          setProposition({
            ...EnrollmentPropositionProperties.mapEnrollmentPropositionToInput(result, {
              cseCode
            }),
            declaredFriends: friends?.data
          })
          return result
        }
      },
      enabled: mode === 'instant'
    })

    const comparisonQueryKey = [
      'enrollment-proposition-comparison',
      enrollmentId,
      propositionId,
      mainProposition?.id
    ]

    const { data: comparison, refetch: refetchComparison } = useQuery({
      queryKey: comparisonQueryKey,
      queryFn: () => {
        if (!hasDifferentProposition || !enrollmentId || !propositionId || !data || data?.isMain)
          return undefined
        return comparePropositionWithMainProposition(enrollmentId, propositionId)
      },
      enabled:
        hasDifferentProposition && !!enrollmentId && !!propositionId && !!data && !data.isMain
    })

    // Clear comparison when proposition becomes main or ids match
    useEffect(() => {
      if (!hasDifferentProposition || data?.isMain) {
        queryClient.removeQueries({ queryKey: comparisonQueryKey })
      }
    }, [
      hasDifferentProposition,
      data?.isMain,
      queryClient,
      enrollmentId,
      propositionId,
      mainProposition?.id
    ])

    // Observer now clears cached comparison (removing highlight)
    const observer = useObserver({
      key: 'enrollment-proposition-comparison',
      update: () => {
        queryClient.removeQueries({ queryKey: comparisonQueryKey })
      }
    })

    const { data: enrollment } = useQuery({
      queryKey: ['enrollment', enrollmentId, enrollmentPropositionDetailsFields],
      queryFn: () => {
        if (enrollmentId) return getEnrollment(enrollmentId, enrollmentPropositionDetailsFields)
      },
      enabled: !!enrollmentId
    })

    const handlePropositionSubmit = async (values: EnrollmentPropositionInput) => {
      if (mode === 'instant') {
        const { declaredFriends, ...input } = values
        const mappedDeclaredFriends = mapDeclaredFriendsToFriendInput(declaredFriends)

        let data: Awaited<ReturnType<typeof mutateAsync>>
        try {
          // update the enrollment friendl wishlist
          await updateEnrollmentMutation({
            id: enrollmentId,
            declaredFriends: mappedDeclaredFriends
          })
          data = await mutateAsync({
            ...mapEnrollmentPropositionInputToRequest(input),
            id: propositionId,
            enrollmentId
          })
        } catch {
          // onError already shows the toast; keep the modals open
          return false
        }
        setProposition({
          ...mapEnrollmentPropositionToInput(data, { cseCode }),
          declaredFriends: mappedDeclaredFriends
        })
        if (data?.isMain) {
          // Immediately clear highlight if this proposition is now main
          queryClient.removeQueries({ queryKey: comparisonQueryKey })
        } else {
          refetchComparison()
        }

        refetchCseRegistration()

        // The proposition price may have changed: refresh every mounted summary (and the
        // payment records that follow it), whatever page hosts this form.
        await observer.notify([
          'enrollment-proposition-summary',
          'enrollment-payment-record',
          'enrollment-profile-monitoring-payment-record',
          'enrollment-overpayment'
        ])

        onSuccess?.()
      } else {
        setProposition(values)
        onPropositionChange?.(values)
      }

      closeModel('create-journey')
      return true
    }

    // Expose the toggleModal function for 'select-insurance' to the parent through the ref
    useImperativeHandle(ref, () => ({ openInsuranceModal: () => toggleModal('select-insurance') }))

    useEffect(() => {
      if (input) setProposition(input)
    }, [input])

    if (mode === 'instant' && isFetching) return <EnrollmentPropositionDetailsSkeleton />

    const mandatoryUnderAge = proposition?.journey?.mandatoryUnderAge
    // the age is read on the departure date of the proposition being edited
    const ageAtDeparture = EnrollmentProperties.getAgeAtDeparture({
      childBirthDate: enrollment?.childBirthDate,
      mainProposition: { journey: proposition?.journey }
    })
    const isUnderAge =
      mandatoryUnderAge && ageAtDeparture !== null && ageAtDeparture < mandatoryUnderAge

    const disabled = enrollment?.status
      ? [
          ApplicationStatusEnum.CONFIRMED,
          ApplicationStatusEnum.CANCELLED_AFTER_CONFIRMATION,
          ApplicationStatusEnum.CANCELLED_BEFORE_CONFIRMATION,
          ApplicationStatusEnum.AFFECTED
        ].includes(enrollment.status)
      : false

    const enrollmentCountry = enrollment?.country ?? country

    // Derived highlight flag
    const highlight = hasDifferentProposition && !!comparison

    return (
      <div className="grid grid-cols-3 gap-4 mt-5">
        <div className="col-span-2 grid grid-cols-2 gap-3">
          <DetailsEditCard
            title={'Séjour'}
            subTitle={proposition?.journey?.reference}
            disabled={disabled}
            onEditClick={() => {
              setIndex(0)
              toggleModal('create-journey')
            }}
          >
            <div className="flex flex-col gap-2">
              <Typography variant="secondary-body" color={tailwindColors.grey.light.active}>
                Pays
              </Typography>
              <Typography
                variant="tag"
                color={
                  !highlight || comparison?.journey?.date?.product?.country?.name
                    ? undefined
                    : tailwindColors.secondary.DEFAULT
                }
              >
                {proposition?.journey?.date.product.country.name}
              </Typography>
              <Typography variant="secondary-body" color={tailwindColors.grey.light.active}>
                Dates et durée de séjour
              </Typography>
              <div className="flex gap-1">
                {proposition?.journey?.date && (
                  <Badge
                    text={`${DateFormatter.DDMMYYYY(proposition?.journey?.date.departureDate)} - ${DateFormatter.DDMMYYYY(proposition?.journey?.date.returnDate)}`}
                    variant={
                      !highlight || comparison?.journey?.date?.departureDate
                        ? undefined
                        : 'warning'
                    }
                  />
                )}
                <Badge
                  text={
                    proposition?.journey?.date?.durationInDays &&
                    getDurationInDaysString(proposition?.journey?.date?.durationInDays)
                  }
                  variant={
                    !highlight || comparison?.journey?.date?.departureDate ? undefined : 'warning'
                  }
                />
              </div>
              <Typography variant="secondary-body" color={tailwindColors.grey.light.active}>
                Formule
              </Typography>
              <Typography
                variant="tag"
                color={
                  !highlight || comparison?.journey?.date?.product?.formula?.translations
                    ? undefined
                    : tailwindColors.secondary.DEFAULT
                }
              >
                {getTranslatedName(proposition?.journey?.date.product.formula)}
              </Typography>
              <Typography variant="secondary-body" color={tailwindColors.grey.light.active}>
                Heures de cours / semaine
              </Typography>
              <Typography
                variant="tag"
                color={
                  !highlight || comparison?.journey?.date?.product?.numberOfHoursPerWeek
                    ? undefined
                    : tailwindColors.secondary.DEFAULT
                }
              >
                {valueRender(proposition?.journey?.date.product.numberOfHoursPerWeek, (hours) =>
                  t('product.number.of.hours.per.week', { hours })
                )}
              </Typography>
              <Typography variant="secondary-body" color={tailwindColors.grey.light.active}>
                Thème
              </Typography>
              <Typography
                variant="tag"
                color={
                  !highlight || comparison?.journey?.mainOption?.translations
                    ? undefined
                    : tailwindColors.secondary.DEFAULT
                }
              >
                {getTranslatedName(proposition?.journey?.mainOption)}
              </Typography>
              {isUnderAge ? <Badge text={"L'enfant est mineur"} variant={'warning'} /> : null}
              <div className="h-4" />
            </div>
          </DetailsEditCard>
          <DetailsEditCard
            title={'Voyage'}
            disabled={disabled}
            onEditClick={() => {
              setIndex(2)
              toggleModal('create-journey')
            }}
          >
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-2">
                <Typography variant="secondary-body" color={tailwindColors.grey.light.active}>
                  {proposition?.mainTrip?.hasTransport === false
                    ? 'Lieu de séjour'
                    : 'Voyage principal'}
                </Typography>
                <Typography
                  variant="tag"
                  color={
                    !highlight ||
                    (comparison?.mainTrip?.departure?.translations &&
                      comparison?.mainTrip?.arrival?.translations &&
                      comparison?.mainTrip?.transportMeans?.translations)
                      ? undefined
                      : tailwindColors.secondary.DEFAULT
                  }
                >
                  {proposition?.mainTrip?.hasTransport === false
                    ? proposition?.preferredPlace?.name ||
                      proposition?.mainTrip?.journeyPlace?.name ||
                      '-'
                    : TripProperties.getTripLabel(proposition?.mainTrip)}
                </Typography>
              </div>
              {proposition?.mainTrip?.hasTransport ? (
                <>
                  <div className="flex flex-col gap-2">
                    <Typography variant="secondary-body" color={tailwindColors.grey.light.active}>
                      Voyage secondaire
                    </Typography>
                    <Typography
                      variant="tag"
                      color={
                        !highlight ||
                        !comparison?.secondaryTrip ||
                        (comparison?.secondaryTrip?.departure?.translations &&
                          comparison?.secondaryTrip?.arrival?.translations &&
                          comparison?.secondaryTrip?.transportMeans?.translations)
                          ? undefined
                          : tailwindColors.secondary.DEFAULT
                      }
                    >
                      {proposition?.secondaryTrip
                        ? TripProperties.getTripLabel(proposition?.secondaryTrip)
                        : '-'}
                    </Typography>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Typography variant="secondary-body" color={tailwindColors.grey.light.active}>
                      Préférence du lieu de séjour
                    </Typography>
                    <Typography
                      variant="tag"
                      color={
                        !highlight || comparison?.preferredPlace?.name
                          ? undefined
                          : tailwindColors.secondary.DEFAULT
                      }
                    >
                      {proposition?.preferredPlace?.name || '-'}
                    </Typography>
                  </div>
                </>
              ) : (
                <Badge text="Sans transport" />
              )}
            </div>
          </DetailsEditCard>
          <DetailsEditCard
            title={'Options'}
            disabled={disabled}
            onEditClick={() => {
              setIndex(1)
              toggleModal('create-journey')
            }}
          >
            <div className="flex flex-col gap-2">
              <Typography variant="secondary-body" color={tailwindColors.grey.light.active}>
                Option(s) facultative(s)
              </Typography>
              <Typography
                variant="tag"
                color={
                  highlight && comparison?.options
                    ? typeof comparison.options === 'boolean'
                      ? comparison.options
                        ? undefined
                        : tailwindColors.secondary.DEFAULT
                      : comparison.options.some((o) => o?.option?.translations)
                        ? undefined
                        : tailwindColors.secondary.DEFAULT
                    : undefined
                }
              >
                {proposition?.options?.map((option) => getTranslatedName(option)).join(', ') ??
                  '-'}
              </Typography>
              <Typography variant="secondary-body" color={tailwindColors.grey.light.active}>
                Modalités d'accueil
              </Typography>
              <Typography
                variant="tag"
                color={
                  !highlight || comparison?.familyArrangement
                    ? undefined
                    : tailwindColors.secondary.DEFAULT
                }
              >
                {proposition?.familyArrangement || '-'}
              </Typography>
              <Typography variant="secondary-body" color={tailwindColors.grey.light.active}>
                Amis du participant
              </Typography>
              {proposition?.declaredFriends && proposition.declaredFriends.length > 0
                ? proposition.declaredFriends.map((friend) => (
                    <Badge key={friend.id} text={toFullName(friend.firstName, friend.lastName)} />
                  ))
                : '-'}
              <div className="h-4" />
            </div>
          </DetailsEditCard>
          {status !== ApplicationStatusEnum.QUOTE && enrollmentId && (
            <DetailsEditCard
              className={'flex flex-col gap-2'}
              title={'Assurances en option'}
              onEditClick={() => toggleModal('select-insurance')}
            >
              {proposition?.insurances && proposition?.insurances.length > 0 ? (
                proposition.insurances.map((insurance, index) => {
                  const comparisonInsurance = InsuranceProperties.getEnrollmentInsuranceName(
                    comparison?.insurances?.[index]
                  )
                  return (
                    <Badge
                      key={insurance.id}
                      text={InsuranceProperties.getEnrollmentInsuranceName(insurance)}
                      variant={!highlight || comparisonInsurance ? undefined : 'warning'}
                    />
                  )
                })
              ) : (
                <Typography variant="secondary-body" color={tailwindColors.grey.light.active}>
                  Aucune assurance sélectionnée
                </Typography>
              )}
            </DetailsEditCard>
          )}
        </div>
        {proposition && (
          <EnrollmentPropositionSummary
            input={{ ...proposition, cseCode }}
            enrollmentId={enrollmentId}
            propositionId={propositionId}
            status={enrollment?.status}
            readonly={disabled}
            mainProposition={mainProposition}
            isClearPromoCodePending={isClearPromoCodePending}
            onInputChange={async (input) => {
              await handlePropositionSubmit(input)
            }}
            onClearPromoCode={() => {
              if (enrollmentId && propositionId)
                clearPromoCodeMutation({ enrollmentId, propositionId })
              else handlePropositionSubmit({ ...proposition, cseCode, promoCode: null })
            }}
          />
        )}
        <EnrollmentPropositionInsuranceModal
          cseCode={cseCode}
          disabled={disabled}
          residenceCountry={enrollmentCountry}
          destinationCountry={proposition?.journey?.date.product.country.id}
          defaultValues={InsuranceProperties.mapEnrollmentInsurancesToInput(
            proposition?.insurances
          )}
          isOpen={modalState['select-insurance']}
          onClose={() => {
            closeModel('select-insurance')
          }}
          onSubmit={async (insurances) => {
            if (await handlePropositionSubmit({ ...proposition, insurances }))
              closeModel('select-insurance')
          }}
        />
        <EnrollmentPropositionFormModal
          defaultIndex={index}
          isOpen={modalState['create-journey']}
          onClose={() => closeModel('create-journey')}
          defaultValues={{ ...proposition, cseCode }}
          handleSubmit={handlePropositionSubmit}
          isLoading={updateEnrollmentPropositionPending || updateEnrollmentPending}
        />
      </div>
    )
  }
)

export default EnrollmentPropositionDetails
